import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { servicesData } from '../mock';
import { Baby, Users, Bus, BookOpen, Palette, Shield, Clock, Heart, Star, ChevronDown, Phone } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';

const iconMap = {
  Baby: <Baby size={32} />,
  Users: <Users size={32} />,
  Bus: <Bus size={32} />,
  BookOpen: <BookOpen size={32} />,
  Palette: <Palette size={32} />
};

export const Childcare = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const data = servicesData.childcare;

  const ageGroups = [
    { range: '0-3 ans', activities: 'Éveil, motricité, premiers apprentissages', color: '#fccc44' },
    { range: '3-6 ans', activities: 'Jeux éducatifs, créativité, socialisation', color: '#55b747' },
    { range: '6-12 ans', activities: 'Aide aux devoirs, activités sportives et artistiques', color: '#0C6951' }
  ];

  const safetyPoints = [
    'Vérification des antécédents judiciaires',
    'Formation aux premiers secours',
    'Diplômes en petite enfance',
    'Assurance responsabilité civile professionnelle'
  ];

  const testimonials = [
    {
      name: 'Famille Moreau',
      rating: 5,
      comment: 'Notre fille de 4 ans adore sa nounou ! Elle revient toujours avec le sourire et nous raconte ses activités.',
      service: 'Garde régulière'
    },
    {
      name: 'Sophie L.',
      rating: 5,
      comment: 'L\'aide aux devoirs a vraiment amélioré les résultats scolaires de mon fils. La nounou est patiente et pédagogue.',
      service: 'Aide aux devoirs'
    }
  ];

  const faqs = [
    {
      question: 'Vos nounous sont-elles diplômées ?',
      answer: 'Oui, toutes nos nounous possèdent au minimum un CAP Petite Enfance ou équivalent, et ont une expérience significative auprès des enfants.'
    },
    {
      question: 'Puis-je rencontrer la nounou avant ?',
      answer: 'Absolument ! Nous organisons systématiquement une rencontre préalable pour que vous puissiez faire connaissance et poser toutes vos questions.'
    },
    {
      question: 'Que se passe-t-il si la nounou est malade ?',
      answer: 'Nous assurons un service de remplacement pour garantir la continuité de la garde de vos enfants.'
    },
    {
      question: 'Proposez-vous la garde en soirée ?',
      answer: 'Oui, nous proposons également des gardes en soirée et le week-end selon vos besoins.'
    }
  ];

  return (
    <div className="page-service">
      <section className="service-hero">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <h1 className="display-medium">{data.title}</h1>
            <p className="body-medium hero-subtitle">{data.description}</p>
          </ScrollReveal>
        </div>
      </section>

      <section className="service-image-section">
        <div className="container">
          <ScrollReveal animation="zoom-in">
            <div className="service-images-dual">
              <img 
                src="/images/childcare.jpg" 
                alt="Garde d'enfants" 
                className="service-dual-image"
              />
              <img 
                src="/images/childcare-2.jpg" 
                alt="Aide aux devoirs" 
                className="service-dual-image"
              />
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Age Groups */}
      <section className="age-groups-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Adapté à Chaque Âge</h2>
              <p className="body-medium">Des activités sur mesure pour tous les âges</p>
            </div>
          </ScrollReveal>
          <div className="age-groups-grid">
            {ageGroups.map((group, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="age-group-card" style={{ borderTopColor: group.color }}>
                  <h3 className="heading-2">{group.range}</h3>
                  <p className="body-medium">{group.activities}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services Details */}
      <section className="service-details">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Nos Services de Garde</h2>
            </div>
          </ScrollReveal>
          <div className="network-grid">
            {data.services.map((service, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="network-card service-detail-card">
                  <div className="feature-icon">{iconMap[service.icon]}</div>
                  <h3 className="heading-2">{service.title}</h3>
                  <p className="body-medium">{service.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="safety-section">
        <div className="container">
          <div className="safety-content">
            <ScrollReveal animation="fade-right">
              <div className="safety-text">
                <Shield size={48} className="safety-icon" />
                <h2 className="heading-1">Sécurité et Confiance</h2>
                <p className="body-medium">
                  La sécurité de vos enfants est notre priorité absolue. Tous nos intervenants passent par un processus de sélection rigoureux.
                </p>
                <ul className="safety-list">
                  {safetyPoints.map((point, index) => (
                    <li key={index} className="body-medium">
                      <Shield size={20} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
            </div>
            </ScrollReveal>
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="safety-stats">
                <div className="stat-badge">
                  <Clock size={32} />
                  <div className="stat-info">
                    <strong>100%</strong>
                    <span>Nounous formées</span>
                  </div>
                </div>
                <div className="stat-badge">
                  <Heart size={32} />
                  <div className="stat-info">
                    <strong>98%</strong>
                    <span>Parents satisfaits</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Avis des Parents</h2>
            </div>
          </ScrollReveal>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="testimonial-card">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} fill="var(--brand-primary)" color="var(--brand-primary)" />
                    ))}
                  </div>
                  <p className="testimonial-comment">"{testimonial.comment}"</p>
                  <div className="testimonial-author">
                    <div className="author-info">
                      <strong>{testimonial.name}</strong>
                      <span>{testimonial.service}</span>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="section-header">
              <h2 className="heading-1">Questions Fréquentes</h2>
            </div>
          </ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <div className="faq-item">
                  <button 
                    className={`faq-question ${openFaq === index ? 'active' : ''}`}
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{faq.question}</span>
                    <ChevronDown size={24} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                  </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p className="body-medium">{faq.answer}</p>
                  </div>
                )}
              </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <ScrollReveal animation="fade-up">
            <div className="cta-content">
              <h2 className="heading-1">Besoin d'une garde d'enfant ?</h2>
              <p className="body-medium">
                Confiez vos enfants à des professionnels qualifiés et bienveillants
              </p>
              <div className="cta-buttons">
                <Link to="/contact#devis" className="btn-primary btn-cta">Demander un devis</Link>
                <a href="tel:0481119591" className="btn-secondary btn-cta">
                  <Phone size={20} />
                  07 69 01 69 04
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};